import { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, StyleSheet } from 'react-native';
import Animated, {
    cancelAnimation,
    Easing,
    interpolate,
    runOnJS,
    useAnimatedStyle,
    useSharedValue,
    withTiming,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    FruitfulLogoIcon,
    H6Emphasized,
    Icon,
    isNonMobileWeb,
    LabelRegular,
    ScrollView,
    Spinner,
    SpinnerSize,
    useWindowDimensions,
    YStack,
} from 'smoothie';

import { BackgroundGradient } from '../../../common/components/BackgroundGradient';
import { useMastraThreads } from '../../hooks/useMastraThreads';

const getThreadTitle = (thread: { title?: string | null }): string => {
    const title = thread.title?.trim();
    if (title != null && title !== '') {
        return title;
    }

    return 'New chat';
};

interface ThreadListProps {
    isOpen: boolean;
    onClose: () => void;
    agentId: string;
    resourceId: string;
    onSelectThread: (threadId: string) => void;
    onNewChat: () => void;
}

export const ThreadList = ({
    isOpen,
    onClose,
    agentId,
    resourceId,
    onSelectThread,
    onNewChat,
}: ThreadListProps) => {
    const { top, bottom } = useSafeAreaInsets();
    const { width: windowWidth } = useWindowDimensions();
    const panelWidth = Math.min(windowWidth * 0.75, 300);

    const [isMounted, setIsMounted] = useState(false);
    const isOpenRef = useRef(isOpen);
    isOpenRef.current = isOpen;
    const progress = useSharedValue(0);
    // Run thread switch / new chat after the close animation so iOS stays smooth.
    const pendingActionRef = useRef<(() => void) | null>(null);

    const finishClose = useCallback(() => {
        if (isOpenRef.current) {
            return;
        }

        setIsMounted(false);
        const pendingAction = pendingActionRef.current;
        pendingActionRef.current = null;
        pendingAction?.();
    }, []);

    const closeThen = useCallback(
        (action?: () => void) => {
            pendingActionRef.current = action ?? null;
            onClose();
        },
        [onClose],
    );

    // Mount when opening. Unmount only after the close animation finishes.
    useEffect(() => {
        if (isOpen) {
            setIsMounted(true);
        }
    }, [isOpen]);

    // Drive slide animation only once the Modal is actually mounted.
    // @ts-expect-error - Animation handlers do not need to return value
    useEffect(() => {
        if (!isMounted) {
            return;
        }

        cancelAnimation(progress);

        if (isOpen) {
            progress.value = 0;
            let innerFrame: number | null = null;
            // Two frames lets the Modal present before we start moving,
            // which avoids the common iOS "jump then animate" hitch.
            const outerFrame = requestAnimationFrame(() => {
                innerFrame = requestAnimationFrame(() => {
                    if (!isOpenRef.current) {
                        return;
                    }
                    progress.value = withTiming(1, {
                        duration: 320,
                        easing: Easing.bezier(0.32, 0.72, 0, 1),
                    });
                });
            });
            return () => {
                cancelAnimationFrame(outerFrame);
                if (innerFrame != null) {
                    cancelAnimationFrame(innerFrame);
                }
            };
        }

        progress.value = withTiming(
            0,
            {
                duration: 260,
                easing: Easing.bezier(0.4, 0, 1, 1),
            },
            (finished) => {
                if (finished === true) {
                    runOnJS(finishClose)();
                }
            },
        );
    }, [isOpen, isMounted, progress, finishClose]);

    const {
        data: threads = [],
        isLoading,
        isFetching,
        refetch,
    } = useMastraThreads({
        agentId,
        resourceId,
        enabled: isMounted,
    });

    useEffect(() => {
        if (isOpen) {
            void refetch();
        }
    }, [isOpen, refetch]);

    const panelStyle = useAnimatedStyle(() => ({
        transform: [
            {
                translateX: interpolate(
                    progress.value,
                    [0, 1],
                    [-panelWidth, 0],
                ),
            },
        ],
    }));

    const backdropStyle = useAnimatedStyle(() => ({
        opacity: interpolate(progress.value, [0, 1], [0, 0.2]),
    }));

    const showSpinner = isLoading || (isFetching && threads.length === 0);
    const showRecents = showSpinner || threads.length > 0;

    if (!isMounted) {
        return null;
    }

    return (
        <Modal
            visible
            transparent
            animationType="none"
            presentationStyle="overFullScreen"
            statusBarTranslucent
            onRequestClose={() => {
                closeThen();
            }}
        >
            <YStack flex={1}>
                <Animated.View
                    pointerEvents="none"
                    style={[
                        StyleSheet.absoluteFillObject,
                        { backgroundColor: '#000' },
                        backdropStyle,
                    ]}
                />
                <YStack
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    onPress={() => {
                        closeThen();
                    }}
                    cursor="pointer"
                />

                <Animated.View
                    style={[
                        {
                            width: panelWidth,
                            height: '100%',
                            overflow: 'hidden',
                            zIndex: 1,
                        },
                        panelStyle,
                    ]}
                >
                    <BackgroundGradient />
                    <YStack
                        flex={1}
                        alignItems="flex-start"
                        paddingTop={top + 24}
                        paddingBottom={bottom + 24}
                        paddingHorizontal={24}
                        gap={48}
                        zIndex={1}
                    >
                        {isNonMobileWeb ? (
                            <Icon
                                icon={FruitfulLogoIcon}
                                size={24}
                                color="$green900"
                            />
                        ) : (
                            <H6Emphasized color="$black">
                                Fruitful AI
                            </H6Emphasized>
                        )}

                        <ScrollView
                            width="100%"
                            flex={1}
                            showsVerticalScrollIndicator={false}
                            contentContainerStyle={{
                                alignItems: 'flex-start',
                                flexGrow: 0,
                            }}
                        >
                            <YStack
                                gap={24}
                                width="100%"
                                alignItems="flex-start"
                            >
                                {showRecents ? (
                                    <YStack
                                        gap={16}
                                        width="100%"
                                        alignItems="flex-start"
                                    >
                                        <LabelRegular color="$gray1000">
                                            Recents
                                        </LabelRegular>

                                        {showSpinner ? (
                                            <YStack
                                                alignItems="flex-start"
                                                paddingVertical={12}
                                            >
                                                <Spinner
                                                    size={SpinnerSize.SMALL}
                                                />
                                            </YStack>
                                        ) : (
                                            <YStack
                                                gap={12}
                                                width="100%"
                                                alignItems="flex-start"
                                            >
                                                {threads.map((thread) => (
                                                    <YStack
                                                        key={thread.id}
                                                        onPress={() => {
                                                            closeThen(() => {
                                                                onSelectThread(
                                                                    thread.id,
                                                                );
                                                            });
                                                        }}
                                                        cursor="pointer"
                                                    >
                                                        <BodyMediumRegular
                                                            color="$black"
                                                            numberOfLines={1}
                                                            textAlign="left"
                                                        >
                                                            {getThreadTitle(
                                                                thread,
                                                            )}
                                                        </BodyMediumRegular>
                                                    </YStack>
                                                ))}
                                            </YStack>
                                        )}
                                    </YStack>
                                ) : null}

                                <Button
                                    variant={ButtonVariant.PRIMARY}
                                    size={ButtonSize.SMALL}
                                    text="Start a new chat"
                                    width="auto"
                                    buttonProps={{
                                        alignSelf: 'flex-start',
                                    }}
                                    onPress={() => {
                                        closeThen(() => {
                                            onNewChat();
                                        });
                                    }}
                                />
                            </YStack>
                        </ScrollView>
                    </YStack>
                </Animated.View>
            </YStack>
        </Modal>
    );
};
